import { CallLeadType, CallOutcome } from "../models/CallQueue";
import { LeadSource } from "../models/Lead";

// Display helpers for the live queue. The rules themselves (priority, max, booking limit, age colour)
// are the API's; these only turn its numbers into words.

export type Tone = "green" | "amber" | "red" | "slate" | "gray";

/** "2008 Audi A3 Sport TDI", skipping whatever is missing. */
export function vehicleTitle(year?: number | null, make?: string | null, model?: string | null): string {
  return [year, make, model].filter((part) => part !== null && part !== undefined && `${part}`.trim() !== "").join(" ");
}

/** Whole hours under two days ("0h", "18h"), whole days after ("6d"). */
export function ageLabel(createdIso: string, now: Date): string {
  const hours = Math.max(0, Math.floor((now.getTime() - new Date(createdIso).getTime()) / 3_600_000));
  return hours < 48 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
}

/** "+£13.80 room" in green, or "£4.76 over max" in red. Colour is never the only signal. */
export function headroomLabel(headroom: number, format: (value: number) => string): { text: string; tone: Tone; overMax: boolean } {
  if (headroom < 0) return { text: `${format(Math.abs(headroom))} over max`, tone: "red", overMax: true };
  return { text: `+${format(headroom)} room`, tone: "green", overMax: false };
}

export interface CallBackPreset {
  label: string;
  at: Date;
}

/** The quick picks beside the call back time: in two hours, and tomorrow at nine. */
export function callBackPresets(now: Date): CallBackPreset[] {
  const tomorrowNine = new Date(now);
  tomorrowNine.setDate(tomorrowNine.getDate() + 1);
  tomorrowNine.setHours(9, 0, 0, 0);

  return [
    { label: "In 2h", at: new Date(now.getTime() + 2 * 3_600_000) },
    { label: "Tomorrow 09:00", at: tomorrowNine },
  ];
}

/** The value a datetime-local input wants, in the browser's own time. */
export function toDateTimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** A datetime-local value back to an instant. Null when it's empty or unreadable. */
export function fromDateTimeLocal(value: string): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * The site the quote came from, as the quotes table shows it. A widget lead's website is only the
 * channel ("Widget"); the site itself, e.g. scrapavehicle, is in its source text.
 */
export function websiteLabel(website: LeadSource | null | undefined, source?: string | null): string {
  const text = source?.trim();
  if (website === LeadSource.Widget && text) return text;
  if (website !== null && website !== undefined && LeadSource[website]) return LeadSource[website];
  return text || "Web quote";
}

export const LEAD_TYPE_LABELS: Record<CallLeadType, string> = {
  [CallLeadType.HighValue]: "High value",
  [CallLeadType.Salvage]: "Salvage",
  [CallLeadType.Local]: "Local",
  [CallLeadType.New]: "New",
};

export const OUTCOME_LABELS: Record<CallOutcome, string> = {
  [CallOutcome.Booked]: "Booked",
  [CallOutcome.CallBack]: "Call back",
  [CallOutcome.NoAnswer]: "No answer",
  [CallOutcome.VoicemailLeft]: "Voicemail left",
  [CallOutcome.PriceTooLow]: "Price too low",
  [CallOutcome.SoldElsewhere]: "Sold elsewhere",
  [CallOutcome.NotInterested]: "Not interested",
  [CallOutcome.WrongNumber]: "Wrong number",
  [CallOutcome.CustomerWillCallBack]: "Customer will call back",
};

/** The outcome grid, in the order the design lays it out (two columns). */
export const OUTCOME_ORDER: CallOutcome[] = [
  CallOutcome.Booked,
  CallOutcome.CallBack,
  CallOutcome.NoAnswer,
  CallOutcome.VoicemailLeft,
  CallOutcome.PriceTooLow,
  CallOutcome.SoldElsewhere,
  CallOutcome.NotInterested,
  CallOutcome.WrongNumber,
  CallOutcome.CustomerWillCallBack,
];

/** Where on the floor-to-max bar a price sits, as a percentage clamped to the bar. */
export function barPosition(value: number, floor: number, max: number): number {
  if (max <= floor) return 100;
  return Math.min(100, Math.max(0, ((value - floor) / (max - floor)) * 100));
}

/** "09:00" from the API's "09:00:00". */
export function shortTime(timeSpan: string | null | undefined): string {
  return (timeSpan ?? "").slice(0, 5);
}
